import apiClient from '../services/apiClient';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import StakeTable, { Column } from "./GameEngine/StakeTable";
import { Calendar } from 'lucide-react';
import { FormMultiSelect } from '../components/ui';
import { formatDateTime, getPresetRangeInTimezone } from '../utils/dateUtils';

interface DepositItem {
  uid: string;
  transaction_id: number;
  transaction_date: string;
  name: string;
  amount: number;
  status: string;
  utr_no: string;
  payment_provider?: string;
  currency?: string;
  gateway_name?: string;
  gateway_method?: string;
  reject_reason?: string;
  transaction_type?: string;
}

type DatePreset = "today" | "yesterday" | "week" | "month" | "custom" | "all";

const getPresetRange = (
  preset: DatePreset
): { start: string | null; end: string | null } => {
  return getPresetRangeInTimezone(preset);
};

const ManualDeposit = () => {
  const currentRole = (localStorage.getItem("role") || "").toLowerCase();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [gatewayFilter, setGatewayFilter] = useState<string[]>([]);
  const [methodFilter, setMethodFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const [activePreset, setActivePreset] = useState<DatePreset>("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const [rejectingItem, setRejectingItem] = useState<DepositItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');



  const { data: depositsData = { data: [], total: 0 }, isLoading } = useQuery({
    queryKey: ['deposits', page, limit, searchTerm, typeFilter, gatewayFilter, methodFilter, statusFilter, activePreset, customStart, customEnd],
    queryFn: async () => {
      const offset = (page - 1) * limit;
      let url = `/getManualDepositData?limit=${limit}&offset=${offset}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (typeFilter.length > 0) url += `&transactionType=${typeFilter.join(',')}`;
      if (statusFilter.length > 0) url += `&status=${statusFilter.join(',')}`;
      if (gatewayFilter.length > 0) url += `&gatewayName=${gatewayFilter.join(',')}`;
      if (methodFilter.length > 0) url += `&method=${methodFilter.join(',')}`;

      if (activePreset === "custom") {
        if (customStart) url += `&startDate=${customStart}`;
        if (customEnd) url += `&endDate=${customEnd}`;
      } else if (activePreset !== "all") {
        const { start, end } = getPresetRange(activePreset);
        if (start) url += `&startDate=${start}`;
        if (end) url += `&endDate=${end}`;
      }

      const response = await apiClient.get(url);
      if (response.data && Array.isArray(response.data.data)) {
        return {
          data: response.data.data,
          total: response.data.total
        };
      }
      return { data: [], total: 0 };
    }
  });

  const deposits = depositsData.data;
  const total = depositsData.total;
  const totalPages = Math.ceil(total / limit);

  const actionMutation = useMutation({
    mutationFn: async ({ transactionId, utr_no, uid, coin, amount, isApproved, rejectReason }: any) => {
      return apiClient.post('/approveRejectManualDeposit', {
        transactionId,
        utr_no,
        uid,
        coin,
        amount,
        isApproved,
        rejectReason,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deposits'] });
    },
    onError: (error) => {
      console.error('Error processing deposit:', error);
    }
  });

  const handleAcceptPayment = (transactionId: number, utrNo: string, uid: string, amount: number, coin: string) => {
    actionMutation.mutate({ transactionId, utr_no: utrNo, uid, coin, amount, isApproved: true });
  };

  const handleOpenRejectModal = (item: DepositItem) => {
    setRejectingItem(item);
    setRejectReason('');
  };

  const handleConfirmReject = () => {
    if (!rejectingItem) return;
    actionMutation.mutate({
      transactionId: rejectingItem.transaction_id,
      utr_no: rejectingItem.utr_no,
      uid: rejectingItem.uid,
      coin: rejectingItem.currency || 'inr',
      amount: rejectingItem.amount,
      isApproved: false,
      rejectReason: rejectReason,
    });
    setRejectingItem(null);
  };

  const getMethodLabel = (provider: string | undefined): string => {
    if (!provider) return 'N/A';
    const labelMap: { [key: string]: string } = {
      'bdpay': 'BD Pay',
      'provider2': 'Wallet',
      'wallet': 'Wallet',
      'bkash': 'bKash',
      'manual': 'Manual',
      'manual_qr': 'Manual QR',
      'manual_upi': 'Manual UPI',
      'manualb_bank': 'Manual Bank',
    };
    return labelMap[provider.toLowerCase()] || provider;
  };

  const filteredData = deposits;

  const columns: Column[] = [
    { key: 'sn', label: 'SNo.', render: (_, __, i) => i + 1 },
    { key: 'name', label: 'User Name', render: (val, item: any) => (
      <div>
        <div className="font-semibold text-white">{val}</div>
        <div className="text-xs text-slate-400">UID: {item.uid}</div>
      </div>
    ) },
    { key: 'parent_name', label: 'User Type', render: (_, item: any) => (
      <span className="text-xs text-slate-300 font-semibold uppercase">
        {item.parent_role ? `${item.parent_role}: ${item.parent_name}` : 'DIRECT USER'}
      </span>
    ) },
    { key: 'amount', label: 'Amount', render: (val) => `${Number(val).toFixed(2)}` },
    { key: 'currency', label: 'Currency', render: (_, item: any) => (item.currency || 'INR').toUpperCase() },
    {
      key: 'gateway_name',
      label: 'Gateway Name',
      render: (_, item: any) => {
        if (item.gateway_name && item.gateway_name !== 'Manual') return item.gateway_name;
        const provider = item.payment_provider || item.transaction_type || '';
        if (provider.startsWith('manual')) {
          const parts = provider.split('_');
          if (parts.length > 1) {
            return parts.slice(1).map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
          }
          return 'Manual';
        }
        return provider.charAt(0).toUpperCase() + provider.slice(1) || 'Manual';
      }
    },
    { key: 'gateway_method', label: 'Gateway Method', render: (_, item: any) => getMethodLabel(item.gateway_method || item.payment_provider) },
    { key: 'utr_no', label: 'TransactionNo' },
    {
      key: 'status',
      label: 'Status',
      render: (val, item: any) => (
        <div className="flex flex-col gap-1">
          <span className={`px-2 py-1 rounded text-xs font-semibold w-fit ${val === 'accepted' ? 'bg-green-500/20 text-green-400' :
            val === 'rejected' ? 'bg-red-500/20 text-red-400' :
              'bg-yellow-500/20 text-yellow-400'
            }`}>
            {String(val).toUpperCase()}
          </span>
          {val === 'rejected' && item.reject_reason && (
            <span className="text-[10px] text-red-400 italic max-w-[150px] truncate block" title={item.reject_reason}>
              Reason: {item.reject_reason}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'transaction_date', label: 'Date', render: (val) => {
        if (!val) return 'N/A';
        let dateVal = val;
        if (typeof val === 'string' && !val.includes('Z') && !val.includes('+')) {
          if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/.test(val)) {
            dateVal = val.replace(' ', 'T') + 'Z';
          }
        }
        return formatDateTime(dateVal);
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, item: DepositItem) => {
        const provider = String(item.payment_provider || item.transaction_type || '').toLowerCase();
        const isOnline = ['nowpayments', 'ccpayment', 'apcopay'].includes(provider);
        if (isOnline) {
          return <span className="text-gray-500">-</span>;
        }
        if (item.status === 'pending') {
          return (
            <div className="flex gap-2">
              <button
                className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-bold transition-colors disabled:opacity-50"
                onClick={() => handleAcceptPayment(item.transaction_id, item.utr_no, item.uid, item.amount, item.currency || 'inr')}
                disabled={actionMutation.isPending}
              >
                Accept
              </button>
              <button
                className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-bold transition-colors disabled:opacity-50"
                onClick={() => handleOpenRejectModal(item)}
                disabled={actionMutation.isPending}
              >
                Reject
              </button>
            </div>
          );
        }
        return <span className="text-gray-500">-</span>;
      }
    }
  ];

  return (
    <div className="ml-0 lg:ml-[256px] p-4 sm:p-6 min-h-screen text-white bg-[#151929]">

      {/* Page Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold font-display tracking-tight text-white">
          Deposit Management
        </h2>
        <button
          onClick={() => setShowFilters(f => !f)}
          className="flex lg:hidden items-center gap-2 px-4 py-2 bg-[#1e2337] border border-slate-700 rounded-lg text-sm font-semibold text-slate-300 hover:text-white hover:border-indigo-500 transition-all"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
          </svg>
          {showFilters ? 'Hide Filters' : 'Filters'}
          {(typeFilter.length + gatewayFilter.length + methodFilter.length + statusFilter.length) > 0 && (
            <span className="ml-1 bg-indigo-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {typeFilter.length + gatewayFilter.length + methodFilter.length + statusFilter.length}
            </span>
          )}
        </button>
      </div>

      {/* Premium Filters Section */}
      <div className={`bg-[#1e2337] p-5 rounded-xl border border-slate-700/40 mb-6 flex-wrap gap-4 items-start ${
        showFilters ? 'flex' : 'hidden lg:flex'
      }`}>


        {/* Type Filter */}
        <div className="w-full sm:w-[140px]">
          <FormMultiSelect
            label="Type"
            selectedValues={typeFilter}
            onChange={(vals) => { setTypeFilter(vals); setPage(1); }}
            placeholder="All Types"
            options={[
              { value: "manual", label: "Manual" },
              { value: "online", label: "Online" }
            ]}
          />
        </div>

        {/* Gateway Filter */}
        <div className="w-full sm:w-[160px]">
          <FormMultiSelect
            label="Gateway Name"
            selectedValues={gatewayFilter}
            onChange={(vals) => { setGatewayFilter(vals); setPage(1); }}
            placeholder="All Gateways"
            options={[
              { value: "manual", label: "Manual" },
              { value: "nowpayments", label: "NowPayments" },
              { value: "ccpayment", label: "CCPayment" },
              { value: "apcopay", label: "Apcopay" }
            ]}
          />
        </div>

        {/* Method Filter */}
        <div className="w-full sm:w-[150px]">
          <FormMultiSelect
            label="Method"
            selectedValues={methodFilter}
            onChange={(vals) => { setMethodFilter(vals); setPage(1); }}
            placeholder="All Methods"
            options={[
              { value: "manual_qr", label: "Manual QR" },
              { value: "manual_upi", label: "Manual UPI" },
              { value: "manualb_bank", label: "Manual Bank" },
              { value: "crypto", label: "Crypto" },
              { value: "wallet", label: "Wallet" }
            ]}
          />
        </div>



        {/* Status Filter */}
        <div className="w-full sm:w-[140px]">
          <FormMultiSelect
            label="Status"
            selectedValues={statusFilter}
            onChange={(vals) => { setStatusFilter(vals); setPage(1); }}
            placeholder="All Statuses"
            options={[
              { value: "pending", label: "Pending" },
              { value: "accepted", label: "Accepted" },
              { value: "rejected", label: "Rejected" }
            ]}
          />
        </div>

        {/* Date Filter */}
        <div className="w-full sm:w-[150px] relative">
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Date Preset</label>
          <div className="relative">
            <select
              value={activePreset}
              onChange={(e) => {
                setActivePreset(e.target.value as DatePreset);
                setPage(1);
              }}
              className="w-full bg-[#151929] border border-slate-700 rounded-lg px-3 py-2 pr-8 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors cursor-pointer appearance-none"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-gray-400">
              <Calendar size={14} />
            </div>
          </div>
        </div>

        {/* Custom date range fields */}
        {activePreset === "custom" && (
          <>
            <div className="w-full sm:w-[140px]">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Start Date</label>
              <input
                type="date"
                value={customStart}
                onChange={(e) => {
                  setCustomStart(e.target.value);
                  setPage(1);
                }}
                className="w-full bg-[#151929] border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors [color-scheme:dark]"
              />
            </div>
            <div className="w-full sm:w-[140px]">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">End Date</label>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => {
                  setCustomEnd(e.target.value);
                  setPage(1);
                }}
                className="w-full bg-[#151929] border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors [color-scheme:dark]"
              />
            </div>
          </>
        )}
      </div>

      <div className="mb-6">
        <StakeTable
          title=""
          columns={columns}
          data={filteredData}
          searchPlaceholder="Search by name, uid, or transaction no..."
          onSearch={(val) => { setSearchTerm(val); setPage(1); }}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onLimitChange={(val) => { setLimit(val); setPage(1); }}
          onPageChange={setPage}
          emptyMessage={isLoading ? "Loading deposits..." : "No deposits found."}
          hasSidebarMargin={false}
        />
      </div>

      {/* Reject Reason Modal */}
      {rejectingItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-[#1e2337] border border-slate-700/60 rounded-xl max-w-md w-full p-6 shadow-2xl animate-in fade-in duration-200">
            <h3 className="text-xl font-bold text-white mb-4">Reject Deposit</h3>
            <p className="text-sm text-slate-400 mb-4">
              Please provide a reason for rejecting the deposit of <strong>{Number(rejectingItem.amount).toFixed(2)} {(rejectingItem.currency || 'INR').toUpperCase()}</strong> for user <strong>{rejectingItem.name}</strong>.
            </p>
            <div className="mb-6">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Rejection Reason</label>
              <textarea
                className="w-full bg-[#151929] border border-slate-700 rounded-lg p-3 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors resize-none"
                rows={4}
                placeholder="Enter reason here..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-bold transition-colors"
                onClick={() => setRejectingItem(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold transition-colors disabled:opacity-50"
                onClick={handleConfirmReject}
                disabled={!rejectReason.trim()}
              >
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualDeposit;
